// 获取节点的矩阵数据，支持序列化字符串，并兼容 node.rotation
export function getNodeMatrix(node) {
  let matrix = node?.transform;
  if (typeof matrix === 'string') {
    matrix = MatrixSerializer.deserialize(matrix);
  }
  // 支持对象形式
  if (
    Array.isArray(matrix) &&
    matrix.length === 6 &&
    matrix.every((v) => typeof v === 'number' && isFinite(v))
  ) {
    return matrix;
  }
  if (typeof matrix === 'object' && matrix !== null) {
    const { a = 1, b = 0, c = 0, d = 1, e = 0, f = 0 } = matrix;
    if ([a, b, c, d, e, f].every((v) => typeof v === 'number' && isFinite(v))) {
      return [a, b, c, d, e, f];
    }
  }

  // 兼容 node.rotation
  if (typeof node?.rotation === 'number' && node.rotation !== 0) {
    const rad = (node.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    // [cos, sin, -sin, cos, 0, 0]
    return [cos, sin, -sin, cos, 0, 0];
  }

  // 兼容 flipX flipY
  if (node?.flipX || node?.flipY) {
    // 构造一个水平/垂直翻转的矩阵
    // flipX: scaleX = -1, flipY: scaleY = -1
    // 以节点中心为对称轴
    const x = node.w < 0 ? node.x + node.w : node.x;
    const y = node.h < 0 ? node.y + node.h : node.y;
    const w = Math.abs(node.w || 0);
    const h = Math.abs(node.h || 0);
    const cx = x + w / 2;
    const cy = y + h / 2;

    // 先平移到中心，再缩放，再平移回去
    // 变换矩阵为: T(-cx, -cy) * S(sx, sy) * T(cx, cy)
    // 其中 S(sx, sy) = [-1, 0, 0, 1] 或 [1, 0, 0, -1] 或 [-1, 0, 0, -1]
    const sx = node.flipX ? -1 : 1;
    const sy = node.flipY ? -1 : 1;

    // 复合矩阵:
    // [sx, 0, 0, sy, cx - sx*cx, cy - sy*cy]
    return [sx, 0, 0, sy, cx - sx * cx, cy - sy * cy];
  }

  return null;
}

export function drawMatrix(renderContext, node, render) {
  const { context } = renderContext;
  // 使用 MatrixSerializer 处理矩阵
  const matrix = getNodeMatrix(node);
  // 如果矩阵为空或格式不对，直接渲染
  if (
    !matrix ||
    !Array.isArray(matrix) ||
    matrix.length !== 6 ||
    matrix.every((v, i) => v === [1, 0, 0, 1, 0, 0][i])
  ) {
    return render();
  }
  // 计算中心点
  const x = node.w < 0 ? node.x + node.w : node.x;
  const y = node.h < 0 ? node.y + node.h : node.y;
  const w = Math.abs(node.w);
  const h = Math.abs(node.h);
  const cx = x + w / 2;
  const cy = y + h / 2;

  context.save();
  try {
    // 先移动到中心点
    context.translate(cx, cy);
    // 应用矩阵变换
    context.transform(matrix[0], matrix[1], matrix[2], matrix[3], matrix[4], matrix[5]);
    // 再移动回原点
    context.translate(-cx, -cy);
    // 渲染内容
    render();
  } finally {
    context.restore();
  }
}

// 矩阵序列化和反序列化工具
export const MatrixSerializer = {
  // 将矩阵序列化为字符串，控制精度，避免出现 0.999999999999999 这种无效小数
  serialize: (matrix) => {
    const formatNum = (v) => {
      // 处理极小数值为0
      if (Math.abs(v) < 1e-10) return '0';
      // 保留最多6位小数，去除多余的0
      return parseFloat(v.toFixed(6)).toString();
    };
    if (!matrix) return '';
    // 支持数组和对象
    if (Array.isArray(matrix)) {
      if (matrix.length === 6 && matrix.every((v) => typeof v === 'number' && isFinite(v))) {
        return matrix.map(formatNum).join(',');
      }
      return '';
    }
    if (typeof matrix === 'object' && matrix !== null) {
      // 允许对象形式
      const { a = 1, b = 0, c = 0, d = 1, e = 0, f = 0 } = matrix;
      if ([a, b, c, d, e, f].every((v) => typeof v === 'number' && isFinite(v))) {
        return [a, b, c, d, e, f].map(formatNum).join(',');
      }
    }
    return '';
  },

  // 将字符串反序列化为矩阵
  deserialize: (matrixStr) => {
    if (!matrixStr || typeof matrixStr !== 'string') return null;
    try {
      // 支持空格和逗号分隔
      const values = matrixStr.split(',').map((v) => parseFloat(v.trim()));
      if (
        values.length === 6 &&
        values.every((v) => typeof v === 'number' && !isNaN(v) && isFinite(v))
      ) {
        return values;
      }
    } catch (e) {
      console.warn('Failed to deserialize matrix:', matrixStr, e);
    }
    return null;
  },
};

// 矩阵变换工具函数
export const MATRIX_UTILS = {
  // 创建单位矩阵
  identity: () => [1, 0, 0, 1, 0, 0],

  // 创建平移矩阵
  translate: (x = 0, y = 0) => [1, 0, 0, 1, x, y],

  // 创建缩放矩阵
  scale: (sx = 1, sy = sx) => [sx, 0, 0, sy, 0, 0],

  // 创建旋转矩阵
  rotate: (angle = 0) => {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return [cos, sin, -sin, cos, 0, 0];
  },

  // 创建水平倾斜矩阵
  skewX: (angle = 0) => [1, 0, Math.tan(angle), 1, 0, 0],

  // 创建垂直倾斜矩阵
  skewY: (angle = 0) => [1, Math.tan(angle), 0, 1, 0, 0],

  // 创建通用倾斜矩阵（后端用 Matrix 形式）
  skew: (skewX = 0, skewY = 0) => {
    // 使用 Matrix 对象作为后端
    return {
      a: 1,
      b: Math.tan(skewY),
      c: Math.tan(skewX),
      d: 1,
      e: 0,
      f: 0,
    };
  },

  // 2D仿射矩阵乘法
  multiply: (m1, m2) => {
    // 容错：支持对象和数组
    const toArr = (m) => {
      if (Array.isArray(m)) return m;
      if (typeof m === 'object' && m !== null) {
        const { a = 1, b = 0, c = 0, d = 1, e = 0, f = 0 } = m;
        return [a, b, c, d, e, f];
      }
      return [1, 0, 0, 1, 0, 0];
    };
    const [a1, b1, c1, d1, e1, f1] = toArr(m1);
    const [a2, b2, c2, d2, e2, f2] = toArr(m2);
    // 2D仿射矩阵乘法（SVG/Canvas标准顺序：m2 * m1，先应用m1再应用m2）
    // 但我们这里约定 multiply(m1, m2) 表示 m2 先应用，m1 后应用（即 m1 * m2）
    // 公式如下：
    return [
      a1 * a2 + c1 * b2,
      b1 * a2 + d1 * b2,
      a1 * c2 + c1 * d2,
      b1 * c2 + d1 * d2,
      a1 * e2 + c1 * f2 + e1,
      b1 * e2 + d1 * f2 + f1,
    ];
  },

  // 2D仿射矩阵逆
  invert: (matrix) => {
    // matrix: [a, b, c, d, e, f]
    if (!Array.isArray(matrix) || matrix.length !== 6) return [1, 0, 0, 1, 0, 0];
    const [a, b, c, d, e, f] = matrix;
    const det = a * d - b * c;
    if (Math.abs(det) < 1e-10) return [1, 0, 0, 1, 0, 0];
    const idet = 1 / det;
    return [
      d * idet,
      -b * idet,
      -c * idet,
      a * idet,
      (c * f - d * e) * idet,
      (b * e - a * f) * idet,
    ];
  },

  // 设置旋转角度，保留原有的缩放、倾斜、平移
  setRotation: (matrix, deg) => {
    // matrix: [a, b, c, d, e, f]
    // deg: 角度
    if (!Array.isArray(matrix) || matrix.length !== 6) {
      matrix = [1, 0, 0, 1, 0, 0];
    }
    const [a, b, c, d, e, f] = matrix;
    // 1. 计算当前 scaleX, scaleY
    const scaleX = Math.hypot(a, b);

    // 2. 计算当前旋转角度
    // const currentRotation = Math.atan2(normB, normA);

    // 3. 计算当前 skewX
    // 先去除旋转影响
    const det = a * d - b * c;
    const scaleY = det / scaleX;
    // 计算 skewX
    const skewX = Math.atan2(a * c + b * d, scaleX * scaleX);

    // 4. 构建新旋转
    const newRad = (deg * Math.PI) / 180;
    const cos = Math.cos(newRad);
    const sin = Math.sin(newRad);

    // 5. 重新组合矩阵
    // 先旋转，再 skewX，再缩放
    // 旋转
    let ra = cos;
    let rb = sin;
    let rc = -sin;
    let rd = cos;
    // skewX
    if (Math.abs(skewX) > 1e-8) {
      const t = Math.tan(skewX);
      rc += t * ra;
      rd += t * rb;
    }
    // scale
    ra *= scaleX;
    rb *= scaleX;
    rc *= scaleY;
    rd *= scaleY;

    // 保留原有平移
    return [ra, rb, rc, rd, e, f];
  },

  // 3D: 生成4x4单位矩阵
  identity3d: () => [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],

  // 3D: 生成绕X轴旋转的4x4矩阵
  rotateX: (angle = 0) => {
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    return [1, 0, 0, 0, 0, c, -s, 0, 0, s, c, 0, 0, 0, 0, 1];
  },

  // 3D: 生成绕Y轴旋转的4x4矩阵
  rotateY: (angle = 0) => {
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    return [c, 0, s, 0, 0, 1, 0, 0, -s, 0, c, 0, 0, 0, 0, 1];
  },

  // 3D: 生成绕Z轴旋转的4x4矩阵
  rotateZ: (angle = 0) => {
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    return [c, s, 0, 0, -s, c, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
  },

  // 3D: 生成缩放4x4矩阵
  scale3d: (sx = 1, sy = 1, sz = 1) => [sx, 0, 0, 0, 0, sy, 0, 0, 0, 0, sz, 0, 0, 0, 0, 1],

  // 3D: 生成平移4x4矩阵
  translate3d: (tx = 0, ty = 0, tz = 0) => [1, 0, 0, tx, 0, 1, 0, ty, 0, 0, 1, tz, 0, 0, 0, 1],

  // 3D: 生成绕任意轴旋转的4x4矩阵
  rotateAxisAngle: (x, y, z, angle) => {
    // 归一化
    const len = Math.sqrt(x * x + y * y + z * z);
    if (len === 0) return MATRIX_UTILS.identity3d();
    x /= len;
    y /= len;
    z /= len;
    const s = Math.sin(angle);
    const c = Math.cos(angle);
    const t = 1 - c;
    return [
      t * x * x + c,
      t * x * y + s * z,
      t * x * z - s * y,
      0,
      t * x * y - s * z,
      t * y * y + c,
      t * y * z + s * x,
      0,
      t * x * z + s * y,
      t * y * z - s * x,
      t * z * z + c,
      0,
      0,
      0,
      0,
      1,
    ];
  },

  // 3D: 4x4矩阵乘法
  multiply3d: (m1, m2) => {
    // m1, m2: 16元素数组
    const a = m1,
      b = m2;
    const r = new Array(16);
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 4; col++) {
        r[row * 4 + col] =
          a[row * 4 + 0] * b[0 * 4 + col] +
          a[row * 4 + 1] * b[1 * 4 + col] +
          a[row * 4 + 2] * b[2 * 4 + col] +
          a[row * 4 + 3] * b[3 * 4 + col];
      }
    }
    return r;
  },
};

// 倾斜变换的预设值
export const SKEW_PRESETS = {
  none: { skewX: 0, skewY: 0 },
  'slight-left': { skewX: -5, skewY: 0 },
  'slight-right': { skewX: 5, skewY: 0 },
  'slight-up': { skewX: 0, skewY: -5 },
  'slight-down': { skewX: 0, skewY: 5 },
  'diagonal-left': { skewX: -15, skewY: -15 },
  'diagonal-right': { skewX: 15, skewY: 15 },
  'extreme-left': { skewX: -30, skewY: 0 },
  'extreme-right': { skewX: 30, skewY: 0 },
  italic: { skewX: -12, skewY: 0 },
  'perspective-left': { skewX: -20, skewY: -5 },
  'perspective-right': { skewX: 20, skewY: -5 },
};

// 获取倾斜角度范围
export const SKEW_RANGE = {
  min: -45,
  max: 45,
  step: 1,
};
